/**
 * Fila de INTENÇÕES da rota (celular), sem dependência de rede ou de React: o envio e o
 * armazenamento são injetados (testável). Regras:
 * - sem conexão, nada é enviado nem emitido: a intenção fica guardada no aparelho;
 * - cada intenção tem UMA chave, usada em todos os envios (o servidor devolve o resultado já
 *   gravado se a resposta se perder — nunca duas vendas ou duas notas);
 * - a mesma intenção nunca é enviada duas vezes ao mesmo tempo neste aparelho;
 * - um resultado concluído nunca volta a "aguardando";
 * - cada intenção pertence a um usuário numa empresa (dono): só o dono vê e envia — o
 *   celular trocado de mão (outro login, outra empresa) nunca manda a venda de outra pessoa.
 */
import type { RouteSaleInput, RouteSaleResult } from "@cafegestor/shared";

export type IntentState = "aguardando" | "enviando" | "concluida" | "erro";
export type RouteIntent = {
  key: string;
  /** usuário|empresa que criou a intenção */
  owner: string;
  label: string;
  body: RouteSaleInput;
  createdAt: string;
  state: IntentState;
  result?: RouteSaleResult;
  error?: string;
  tries: number;
};

export type KeyValueStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export type QueueDeps = {
  storage: () => KeyValueStorage | null;
  /** dono atual (usuário|empresa); null sem sessão */
  owner: () => string | null;
  send: (intent: RouteIntent) => Promise<RouteSaleResult>;
  /** erro que não muda ao repetir (dados, permissão): para a intenção para revisão */
  isDefinitive: (err: unknown) => boolean;
  isOnline: () => boolean;
  newKey: () => string;
  now: () => string;
};

export const STORE_KEY = "cafegestor.rota.intencoes";

export function createIntentQueue(deps: QueueDeps) {
  let memory: RouteIntent[] = [];
  let loaded = false;
  let view: { from: RouteIntent[]; owner: string | null; list: RouteIntent[] } | null = null;
  const inFlight = new Set<string>();
  const listeners = new Set<() => void>();

  function readStorage(): RouteIntent[] {
    try {
      const raw = deps.storage()?.getItem(STORE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as RouteIntent[]) : [];
    } catch {
      return [];
    }
  }

  /** Todas as intenções do aparelho (de qualquer dono). */
  function all(): RouteIntent[] {
    if (!loaded) {
      memory = readStorage();
      loaded = true;
    }
    return memory;
  }

  /**
   * Intenções do dono atual. Mesma referência enquanto nada mudar (snapshot estável para o
   * React).
   */
  function list(): RouteIntent[] {
    const from = all();
    const owner = deps.owner();
    if (!view || view.from !== from || view.owner !== owner) {
      view = { from, owner, list: owner ? from.filter((i) => i.owner === owner) : [] };
    }
    return view.list;
  }

  /** O dono mudou (login, troca de empresa): avisa a tela. */
  function ownerChanged() {
    for (const l of listeners) l();
  }

  function persist(next: RouteIntent[]) {
    memory = next;
    loaded = true;
    try {
      deps.storage()?.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* sem armazenamento: fica só em memória enquanto a tela estiver aberta */
    }
    for (const l of listeners) l();
  }

  /** Recarrega do armazenamento (ex.: outra aba mudou a fila). */
  function reload() {
    memory = readStorage();
    loaded = true;
    for (const l of listeners) l();
  }

  function subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  function add(label: string, body: RouteSaleInput): RouteIntent {
    const owner = deps.owner();
    if (!owner) throw new Error("Entre com seu usuário e escolha a empresa antes de vender.");
    const intent: RouteIntent = {
      key: deps.newKey(),
      owner,
      label,
      body,
      createdAt: deps.now(),
      state: "aguardando",
      tries: 0,
    };
    persist([intent, ...all()]);
    return intent;
  }

  function find(key: string) {
    return all().find((i) => i.key === key);
  }

  function remove(key: string) {
    // não some com uma intenção no meio do envio: o resultado precisa ser visto
    if (inFlight.has(key)) return;
    persist(all().filter((i) => i.key !== key || i.owner !== deps.owner()));
  }

  /** Saída do usuário: apaga o que já foi concluído (pendentes ficam, só para o dono). */
  function forgetCompleted() {
    persist(all().filter((i) => i.state !== "concluida"));
  }

  function update(key: string, patch: Partial<RouteIntent>) {
    persist(all().map((i) => (i.key === key ? { ...i, ...patch } : i)));
  }

  /** Envia uma intenção (sempre com a mesma chave). Sem conexão, ela continua aguardando. */
  async function send(key: string): Promise<RouteIntent | undefined> {
    const current = find(key);
    if (!current || current.state === "concluida" || inFlight.has(key)) return current;
    // nunca envia a intenção de outro usuário/empresa com a sessão atual
    if (current.owner !== deps.owner()) return current;
    if (!deps.isOnline()) return current;
    inFlight.add(key);
    update(key, { state: "enviando", tries: current.tries + 1 });
    try {
      const result = await deps.send(current);
      update(key, { state: "concluida", result, error: undefined });
    } catch (err) {
      if (find(key)?.state !== "concluida") {
        update(key, {
          state: deps.isDefinitive(err) ? "erro" : "aguardando",
          error: err instanceof Error ? err.message : "Falha de conexão.",
        });
      }
    } finally {
      inFlight.delete(key);
    }
    return find(key);
  }

  let flushing = false;
  /** Envia as pendentes, uma de cada vez, na ordem em que foram feitas. */
  async function flush(): Promise<void> {
    if (flushing) return;
    flushing = true;
    try {
      const pending = list()
        .filter((i) => i.state === "aguardando" || i.state === "enviando")
        .reverse()
        .map((i) => i.key);
      for (const key of pending) {
        if (!deps.isOnline()) break;
        await send(key);
      }
    } finally {
      flushing = false;
    }
  }

  return { list, reload, subscribe, add, remove, send, flush, ownerChanged, forgetCompleted };
}
